import { type Component, createEffect, createSignal, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Switch from '~/components/Switch'
import { COMPRESS_LEVELS, type CompressLevelType, type CompressResult, compressImage, formatBytes, levelOptions } from './imageCompression'

/**
 * Tamaño máximo del archivo que se acepta **antes** de comprimir. Es holgado a
 * propósito: la imagen se reduce y se recodifica antes de incrustarla, así que
 * una foto de móvil entra sin problema y acaba pesando una fracción.
 */
export const MAX_IMAGE_BYTES = 12 * 1024 * 1024

export interface LinkValue {
  text: string
  href: string
  newTab: boolean
}

interface LinkDialogProps {
  isOpen: boolean
  /** Texto seleccionado o enlace existente al abrir. */
  initial: LinkValue
  /** Verdadero si el cursor está sobre un enlace: habilita "Quitar enlace". */
  editing: boolean
  onClose: () => void
  onSubmit: (value: LinkValue) => void
  onRemove: () => void
}

/**
 * Diálogo de enlace. Sustituye al `prompt()` del navegador, que no dejaba poner
 * el texto ni elegir si abre en otra pestaña, y que en varios navegadores se
 * come la selección al abrirse.
 */
export const LinkDialog: Component<LinkDialogProps> = props => {
  const [text, setText] = createSignal('')
  const [href, setHref] = createSignal('')
  const [newTab, setNewTab] = createSignal(true)

  // Los valores se siembran al abrir: sembrar en el montaje dejaría el diálogo
  // con los datos del enlace anterior.
  createEffect(() => {
    if (!props.isOpen) return
    setText(props.initial.text)
    setHref(props.initial.href)
    setNewTab(props.initial.newTab)
  })

  const submit = () => {
    const url = href().trim()
    if (!url) return
    props.onSubmit({ text: text().trim() || url, href: url, newTab: newTab() })
  }

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="sm">
      <Modal.Header>
        <h3 class="text-lg font-semibold text-foreground">{props.editing ? 'Editar enlace' : 'Insertar enlace'}</h3>
      </Modal.Header>
      <Modal.Body>
        <div class="space-y-4">
          <Input label="Texto" value={text()} onInput={e => setText(e.currentTarget.value)} placeholder="Texto que se ve" />
          <Input
            label="Dirección"
            value={href()}
            onInput={e => setHref(e.currentTarget.value)}
            placeholder="https://ejemplo.com"
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') submit()
            }}
          />
          <Switch label="Abrir en una pestaña nueva" checked={newTab()} onChange={e => setNewTab((e.currentTarget as HTMLInputElement).checked)} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div class="flex w-full items-center justify-between gap-2">
          <Show when={props.editing}>
            <Button type="button" variant="text" color="danger" onClick={props.onRemove} prependIcon="material-symbols-light:link-off">
              Quitar enlace
            </Button>
          </Show>
          <div class="ml-auto flex gap-2">
            <Button type="button" variant="soft" color="default" onClick={props.onClose}>
              Cancelar
            </Button>
            <Button type="button" onClick={submit} disabled={!href().trim()}>
              {props.editing ? 'Actualizar' : 'Insertar'}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export interface ImageValue {
  src: string
  alt: string
  width: number | null
  align: 'left' | 'center' | 'right'
}

interface ImageDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (value: ImageValue) => void
  onError: (message: string) => void
}

/**
 * Diálogo de imagen: subir un archivo del equipo o pegar una dirección, con
 * vista previa, texto alternativo, ancho y alineación.
 *
 * Antes era un `prompt()` pidiendo una URL, que no servía para las imágenes del
 * propio equipo (aquí se guardan incrustadas, no hay almacenamiento de ficheros).
 */
export const ImageDialog: Component<ImageDialogProps> = props => {
  const [src, setSrc] = createSignal('')
  const [alt, setAlt] = createSignal('')
  const [width, setWidth] = createSignal('')
  const [align, setAlign] = createSignal<'left' | 'center' | 'right'>('center')
  const [procesando, setProcesando] = createSignal(false)
  const [peso, setPeso] = createSignal<CompressResult | null>(null)
  const [nivel, setNivel] = createSignal<CompressLevelType>('equilibrada')
  /** El archivo se conserva para poder recomprimir al cambiar de nivel. */
  const [archivo, setArchivo] = createSignal<File | null>(null)

  createEffect(() => {
    if (!props.isOpen) return
    setSrc('')
    setAlt('')
    setWidth('')
    setAlign('center')
    setPeso(null)
    setProcesando(false)
    setArchivo(null)
    setNivel('equilibrada')
  })

  const readFile = async (file?: File) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      props.onError('El archivo no es una imagen')
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      props.onError(`La imagen no puede superar ${formatBytes(MAX_IMAGE_BYTES)}`)
      return
    }

    setArchivo(file)
    if (!alt()) setAlt(file.name.replace(/\.[^.]+$/, ''))
    await comprimir(file, nivel())
  }

  /**
   * Comprime con el nivel elegido. Se llama al elegir archivo y cada vez que se
   * cambia el nivel, siempre desde el archivo original: recomprimir sobre lo ya
   * comprimido iría perdiendo calidad en cada paso.
   */
  const comprimir = async (file: File, level: CompressLevelType) => {
    setProcesando(true)
    try {
      const resultado = await compressImage(file, levelOptions(level))
      setSrc(resultado.dataUrl)
      setPeso(resultado)
    } catch (error) {
      props.onError(error instanceof Error ? error.message : 'No pudimos leer la imagen')
    } finally {
      setProcesando(false)
    }
  }

  const cambiarNivel = (level: CompressLevelType) => {
    setNivel(level)
    const file = archivo()
    if (file) void comprimir(file, level)
  }

  const submit = () => {
    if (!src()) return
    const parsed = Number.parseInt(width(), 10)
    props.onSubmit({ src: src(), alt: alt(), width: Number.isFinite(parsed) && parsed > 0 ? parsed : null, align: align() })
  }

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="md">
      <Modal.Header>
        <h3 class="text-lg font-semibold text-foreground">Insertar imagen</h3>
      </Modal.Header>
      <Modal.Body>
        <div class="space-y-4">
          <div class="flex flex-wrap items-center gap-3">
            {/* La etiqueta abre el selector de forma nativa: llamar a click()
                sobre un input oculto no funciona en todos los navegadores. */}
            <label class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-info-soft px-3 py-2 text-sm font-medium text-info transition-colors hover:bg-info-soft/70">
              <Icon icon="material-symbols-light:upload" size={20} />
              Subir del equipo
              <input
                type="file"
                accept="image/*"
                class="sr-only"
                onClick={e => {
                  e.currentTarget.value = ''
                }}
                onChange={e => readFile(e.currentTarget.files?.[0])}
              />
            </label>
            <span class="text-xs text-foreground-subtle">PNG, JPG, WebP o SVG. Se comprime al insertarla.</span>
          </div>

          <Input label="O dirección de la imagen" value={src().startsWith('data:') ? '' : src()} onInput={e => setSrc(e.currentTarget.value)} placeholder="https://ejemplo.com/foto.png" />

          <Show when={archivo()}>
            <div>
              <span class="mb-1 block text-xs font-medium text-foreground-subtle">Compresión</span>
              <div class="flex flex-wrap gap-2">
                <For each={Object.entries(COMPRESS_LEVELS) as [CompressLevelType, (typeof COMPRESS_LEVELS)[CompressLevelType]][]}>
                  {([key, config]) => (
                    <Button
                      type="button"
                      size="sm"
                      variant={nivel() === key ? 'soft' : 'text'}
                      color={nivel() === key ? 'primary' : 'default'}
                      disabled={procesando()}
                      tooltip={config.hint}
                      onClick={() => cambiarNivel(key)}
                    >
                      {config.title}
                    </Button>
                  )}
                </For>
              </div>
            </div>
          </Show>

          <Show when={procesando()}>
            <p class="text-sm text-foreground-subtle">Comprimiendo la imagen...</p>
          </Show>

          <Show when={src()}>
            <div class="rounded-lg border border-border bg-surface-muted p-3 text-center">
              <img src={src()} alt={alt() || 'Vista previa'} class="mx-auto max-h-48 max-w-full object-contain" />
              <Show when={peso()}>
                {info => (
                  <p class="mt-2 text-xs text-foreground-subtle">
                    <Show when={info().compressed} fallback={<>Se incrusta tal cual: {formatBytes(info().bytes)}</>}>
                      Comprimida: {formatBytes(info().originalBytes)} → <strong class="text-success">{formatBytes(info().bytes)}</strong> ({info().width}×{info().height} px)
                    </Show>
                  </p>
                )}
              </Show>
            </div>
          </Show>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Texto alternativo" value={alt()} onInput={e => setAlt(e.currentTarget.value)} placeholder="Descripción de la imagen" />
            <Input label="Ancho (px)" type="number" min="24" value={width()} onInput={e => setWidth(e.currentTarget.value)} placeholder="Automático" />
          </div>

          <div>
            <span class="mb-1 block text-xs font-medium text-foreground-subtle">Alineación</span>
            <div class="flex gap-2">
              <Button type="button" size="sm" variant={align() === 'left' ? 'soft' : 'text'} color={align() === 'left' ? 'primary' : 'default'} onClick={() => setAlign('left')}>
                Izquierda
              </Button>
              <Button type="button" size="sm" variant={align() === 'center' ? 'soft' : 'text'} color={align() === 'center' ? 'primary' : 'default'} onClick={() => setAlign('center')}>
                Centro
              </Button>
              <Button type="button" size="sm" variant={align() === 'right' ? 'soft' : 'text'} color={align() === 'right' ? 'primary' : 'default'} onClick={() => setAlign('right')}>
                Derecha
              </Button>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="soft" color="default" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={submit} disabled={!src() || procesando()}>
            {procesando() ? 'Comprimiendo...' : 'Insertar'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
